/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, forwardRef } from "react";
import DatePicker from "react-datepicker";
import SearchMultiSelect from "../components/SearchMultiSelect";
import dayjs from "dayjs";

const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
  <div ref={ref} className="date-custom-input" onClick={onClick}>
    <div className="date-custom-input-content">{value}</div>
    <i className="ti ti-calendar" style={{ color: "#aaa" }}></i>
  </div>
));

export const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const DateRangePicker = ({
  defaultEnabled = true,
  optional = false,
  label,
  mode,
  initialEnd,
  initialStart,
  additionalMonths = 3,
  onChangeValue = () => null,
}) => {
  const [enabled, setEnabled] = useState(defaultEnabled);
  const [startDate, setStartDate] = useState(new Date(initialStart || "1/1/2020"));
  const [endDate, setEndDate] = useState(new Date(initialEnd || "6/30/2020"));
  const [options, setOptions] = useState([]);
  const [optionsEnd, setOptionsEnd] = useState([]);

  useEffect(() => {
    if (!enabled) onChangeValue({ startDate: null, endDate: null });
    else onChangeValue({ startDate: startDate, endDate: endDate });
  }, [endDate, startDate, enabled]);

  const getDate = (date) => [months[date.getMonth()], date.getFullYear()].join(" ");

  const dateOptions = (isEnd) => {
    const arr = [];
    const monthsDifference = dayjs().diff(new Date(startDate), "month") + additionalMonths;
    for (let x = 0; x <= monthsDifference; x++) {
      const date = new Date(startDate);
      date.setMonth(date.getMonth() + x);
      if (isEnd) {
        date.setMonth(date.getMonth() + 1);
        date.setDate(date.getDate() - 1);
      }
      arr.push({
        value: date.toLocaleDateString(),
        label: getDate(date),
      });
    }
    if (isEnd) setOptionsEnd(arr);
    else setOptions(arr);
  };

  useEffect(() => {
    dateOptions();
    dateOptions(true);
  }, []);
  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        {optional ? (
          <input
            type="checkbox"
            style={{ margin: 0, marginBottom: 3, marginRight: 3 }}
            checked={enabled}
            onChange={() => setEnabled(!enabled)}
          />
        ) : null}
        <label htmlFor="from" className="date-range-picker-label">
          {label || "Date"}
        </label>
      </div>
      {(optional && enabled) || !optional ? (
        <div className="date-range-picker-container" style={{ marginBottom: 15 }}>
          {mode === "dropdown" ? (
            <div style={{ width: 130 }}>
              <SearchMultiSelect
                smallOptions
                small
                options={options}
                maxSelect={1}
                hasPreview={false}
                forceHasSelectAll={false}
                onChange={(data) => {
                  setStartDate(new Date(data));
                }}
                initialSelected={[{ value: new Date(startDate).toLocaleDateString() }]}
              />
            </div>
          ) : (
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              style={{ display: "flex", flex: 1 }}
              name="from"
              customInput={<CustomDateInput />}
              dateFormat="dd MMM yy"
            />
          )}
          <div style={{ margin: "0 10px" }}>To:</div>
          {mode === "dropdown" ? (
            <div style={{ width: 130 }}>
              <SearchMultiSelect
                smallOptions
                small
                options={optionsEnd}
                maxSelect={1}
                hasPreview={false}
                forceHasSelectAll={false}
                onChange={(data) => {
                  setEndDate(new Date(data));
                }}
                initialSelected={[{ value: new Date(endDate).toLocaleDateString() }]}
              />
            </div>
          ) : (
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              style={{ display: "flex", flex: 1 }}
              minDate={startDate}
              customInput={<CustomDateInput />}
              name="to"
              dateFormat="dd MMM yy"
            />
          )}
        </div>
      ) : (
        <div style={{ height: 41.833, marginBottom: 15 }} />
      )}
    </>
  );
};

export default DateRangePicker;
